import { ImageComponent, ReactPortalComp } from "@tm/static";
import React, { useCallback, useMemo, useState } from "react";

import cancelGreyBg from "../../../../../icons/cancelGreyBg.svg";
import type { AddBankDetailData, AddBankDetailField } from "../../../types";
import {
  CloseButton,
  FieldInput,
  FieldLabel,
  FieldWrapper,
  ModalBody,
  ModalHeader,
  ModalPanel,
  ModalTitle,
  Overlay,
  SubmitButton,
} from "./BankDetailsModal.style";

interface BankDetailsModalProps {
  data?: AddBankDetailData;
  onSubmit?: (values: Record<string, string>) => void;
  onClose: () => void;
}

function getTitle(data?: AddBankDetailData): string {
  return data?.title?.text?.value || "Add Bank Details";
}

function getSubmitLabel(data?: AddBankDetailData): string {
  return data?.submitButton?.text?.value || "Submit Details";
}

function BankDetailField({
  field,
  value,
  onChange,
}: {
  field: AddBankDetailField;
  value: string;
  onChange: (key: string, val: string) => void;
}) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(field.key ?? "", e.target.value);
    },
    [field.key, onChange]
  );

  if (!field.key) return null;

  return (
    <FieldWrapper>
      <FieldLabel htmlFor={`bank-field-${field.key}`}>
        {field.placeholder}
      </FieldLabel>
      <FieldInput
        id={`bank-field-${field.key}`}
        type={field.type || "text"}
        value={value}
        onChange={handleChange}
        placeholder={field.placeholder}
      />
    </FieldWrapper>
  );
}

export function BankDetailsModal({
  data,
  onSubmit,
  onClose,
}: BankDetailsModalProps) {
  const [formValues, setFormValues] = useState<Record<string, string>>({});

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handleFieldChange = useCallback((key: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    onSubmit?.(formValues);
    onClose();
  }, [formValues, onSubmit, onClose]);

  const fields = data?.fields ?? [];
  const title = useMemo(() => getTitle(data), [data]);
  const submitLabel = useMemo(() => getSubmitLabel(data), [data]);

  const isSubmitDisabled = fields.some(
    (f) => f.key && !formValues[f.key]?.trim()
  );

  return (
    <ReactPortalComp>
      <Overlay onClick={handleOverlayClick}>
        <ModalPanel role="dialog" aria-modal="true" aria-label={title}>
          <ModalHeader>
            <ModalTitle>{title}</ModalTitle>
            <CloseButton onClick={onClose} aria-label="Close">
              <ImageComponent
                src={cancelGreyBg.src}
                width="24px"
                height="24px"
                alt="Close"
              />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            {fields.map((field, index) => (
              <BankDetailField
                key={field.key ?? field.placeholder ?? index}
                field={field}
                value={formValues[field.key ?? ""] ?? ""}
                onChange={handleFieldChange}
              />
            ))}

            <SubmitButton
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              type="button"
            >
              {submitLabel}
            </SubmitButton>
          </ModalBody>
        </ModalPanel>
      </Overlay>
    </ReactPortalComp>
  );
}
